import { getOAuthProviders, PROVIDER_DEFAULT_MODELS } from '@mastra/code-sdk/auth/storage';
import { LoginDialogComponent } from '../components/login-dialog.js';
import { promptAuthMode } from '../components/login-mode-selector.js';
import { LoginSelectorComponent } from '../components/login-selector.js';
import { seedOMDefaultAfterLogin } from '../om-defaults.js';
import { showModalOverlay } from '../overlay.js';
import type { SlashCommandContext } from './types.js';

async function performLogin(ctx: SlashCommandContext, providerId: string): Promise<void> {
  const provider = getOAuthProviders().find(p => p.id === providerId);
  const providerName = provider?.name || providerId;

  if (!ctx.authStorage) {
    ctx.showError('Auth storage not configured');
    return;
  }

  const authMode = await promptAuthMode(ctx.state.ui, providerName, provider?.authModes);
  if (authMode === null) {
    // User cancelled at the mode-selection step.
    return;
  }

  return new Promise(resolve => {
    const dialog = new LoginDialogComponent(ctx.state.ui, providerId, (success, message) => {
      ctx.state.ui.hideOverlay();
      if (success) {
        ctx.showInfo(`Successfully logged in to ${providerName}`);
      } else if (message) {
        ctx.showInfo(message);
      }
      resolve();
    });

    showModalOverlay(ctx.state.ui, dialog, { widthPercent: 0.8, maxHeight: '60%' });
    dialog.focused = true;

    ctx
      .authStorage!.login(providerId, {
        onAuth: (info: { url: string; instructions?: string }) => {
          dialog.showAuth(info.url, info.instructions);
        },
        onPrompt: async (prompt: { message: string; placeholder?: string }) => {
          return dialog.showPrompt(prompt.message, prompt.placeholder);
        },
        onProgress: (message: string) => {
          dialog.showProgress(message);
        },
        signal: dialog.signal,
        authMode,
      })
      .then(async () => {
        ctx.state.ui.hideOverlay();

        // The `/login` command must not change the user's active model or model
        // pack — that only belongs to the onboarding flow. Only auto-select the
        // provider default when no model is selected yet (e.g. onboarding was
        // skipped), so the user isn't left without a usable model.
        const hasSelectedModel = ctx.state.session.model.get() !== '';
        const defaultModel = PROVIDER_DEFAULT_MODELS[providerId as keyof typeof PROVIDER_DEFAULT_MODELS];
        if (defaultModel && !hasSelectedModel) {
          await ctx.state.session.model.switch({ modelId: defaultModel });
          ctx.showInfo(`Logged in to ${providerName} - switched to ${defaultModel}`);
        } else {
          ctx.showInfo(`Successfully logged in to ${providerName}`);
        }
        await seedOMDefaultAfterLogin(ctx.state, providerId, message => ctx.showInfo(message));

        resolve();
      })
      .catch((error: Error) => {
        ctx.state.ui.hideOverlay();
        if (error.message !== 'Login cancelled') {
          ctx.showError(`Failed to login: ${error.message}`);
        }
        resolve();
      });
  });
}

export async function handleLoginCommand(ctx: SlashCommandContext, mode: 'login' | 'logout'): Promise<void> {
  const allProviders = getOAuthProviders();
  const loggedInIds = allProviders.filter(p => ctx.authStorage?.isLoggedIn(p.id)).map(p => p.id);

  if (mode === 'logout') {
    if (loggedInIds.length === 0) {
      ctx.showInfo('No OAuth providers logged in. Use /login first.');
      return;
    }
  }

  const providers = mode === 'logout' ? allProviders.filter(p => loggedInIds.includes(p.id)) : allProviders;

  if (providers.length === 0) {
    ctx.showInfo('No OAuth providers available.');
    return;
  }

  if (mode === 'login') {
    ctx.analytics?.trackInteractivePrompt('login_provider_selector', {
      threadId: ctx.state.session.thread.getId(),
      resourceId: ctx.state.session.identity.getResourceId(),
      mode: ctx.state.session.mode.get(),
    });
  }

  return new Promise<void>(resolve => {
    const selector = new LoginSelectorComponent(
      mode,
      {
        getOAuthProviders: () => providers,
        isLoggedIn: providerId => loggedInIds.includes(providerId),
      },
      async providerId => {
        ctx.state.ui.hideOverlay();
        const provider = providers.find(p => p.id === providerId);
        if (provider) {
          if (mode === 'login') {
            await performLogin(ctx, provider.id);
          } else {
            if (ctx.authStorage) {
              ctx.authStorage.logout(provider.id);
              ctx.showInfo(`Logged out from ${provider.name}`);
            } else {
              ctx.showError('Auth storage not configured');
            }
          }
        }
        resolve();
      },
      () => {
        ctx.state.ui.hideOverlay();
        resolve();
      },
    );

    showModalOverlay(ctx.state.ui, selector, { widthPercent: 0.8, maxHeight: '60%' });
  });
}
